import { ButtonVariant } from "@mantine/core";
import { ClientInferResponseBody } from "@ts-rest/core";
import { t } from "i18next";
import { createContext, CSSProperties, PropsWithChildren, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { contract } from "#app/http/api";
import { showErrorToast } from "#app/notifications";
import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { useUpdateCluster } from "#modules/similarPart/infra/clustering/useUpdateCluster";
import { QuoteRequestLinesSimilarPartDto } from "#modules/similarPart/infra/searchQuoteRequestLineSimilarParts.api";
import { SearchSimilarPartDto } from "#modules/similarPart/infra/searchSimilarParts.api";
import { useClusterToggleSelect } from "#modules/similarPart/view/clustering/useClusterToggleSelect";
import { useDataChunkSize } from "#shared/helpers/useDataChunkSize";
import { usePage } from "#shared/helpers/usePage";
import { Icon } from "#shared/icons/Icon";
import { ROUTES } from "#shared/routes";
import { Flex } from "#shared/view/components/Flex";
import { Grid } from "#shared/view/components/Grid";
import { InlineLoader } from "#shared/view/components/InlineLoader";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";

export const PAGE_SIZE = 10;

type SearchResultContext<T extends SearchSimilarPartDto | QuoteRequestLinesSimilarPartDto> = {
  similarParts: T[] | undefined | null;
  cluster: TargetDto[];
  sourceQuoteRequestLine: QuoteRequestLineDto | undefined | null;
};

const SearchResultContext = createContext<
  SearchResultContext<SearchSimilarPartDto | QuoteRequestLinesSimilarPartDto>
>({
  similarParts: [],
  cluster: [],
  sourceQuoteRequestLine: null,
});

type Props<T extends SearchSimilarPartDto | QuoteRequestLinesSimilarPartDto> = {
  similarParts: T[] | undefined | null;
  sourcePart?: PartDto | null;
  columnCount?: number;
  sourceQuoteRequestLine?: QuoteRequestLineDto | null;
  Card: React.FC<{
    targetPart: T;
    sourcePart?: PartDto | undefined;
    isSelected?: boolean;
    handleToggleSelect?: () => void;
    featuresDisplayMode?: "truncate" | "all" | "none";
    index: number;
  }>;
};

export type TargetDto = NonNullable<
  ClientInferResponseBody<typeof contract.quoteRequestLines.findOne>["cluster"]
>["targetQuoteRequestLines"][number];

export const SearchResult = <T extends SearchSimilarPartDto | QuoteRequestLinesSimilarPartDto>({
  children,
  similarParts,
  sourcePart,
  columnCount = 3,
  sourceQuoteRequestLine,
  Card,
}: PropsWithChildren & Props<T>) => {
  const totalColumns = 12;
  const columnSpan = totalColumns / columnCount;

  const targets: TargetDto[] = sourceQuoteRequestLine?.cluster?.targetQuoteRequestLines ?? [];

  const [cluster, setCluster] = useState<TargetDto[]>(targets);

  const { isSelected, handleToggleSelect } = useClusterToggleSelect(cluster, setCluster);

  return (
    <SearchResultContext.Provider value={{ similarParts, cluster, sourceQuoteRequestLine }}>
      <Stack mt={"sm"} justify="space-between" mih="calc(100vh - var(--header-height) - 110px)">
        <Grid w={"100%"} columns={totalColumns} gap="xs">
          {similarParts?.map((similarPart, index) => (
            <Grid.Col span={columnSpan} key={similarPart.id}>
              <Card
                targetPart={similarPart}
                sourcePart={sourcePart || undefined}
                isSelected={isSelected(similarPart as QuoteRequestLinesSimilarPartDto)}
                handleToggleSelect={() =>
                  handleToggleSelect(similarPart as QuoteRequestLinesSimilarPartDto)
                }
                index={index}
              />
            </Grid.Col>
          ))}
        </Grid>

        {children}
      </Stack>
    </SearchResultContext.Provider>
  );
};

SearchResult.MoreButton = function SearchResultMoreButton({
  isFetching,
  variant = "filled",
  style,
  ...props
}: {
  isFetching?: boolean;
  variant?: ButtonVariant;
  style?: CSSProperties;
}) {
  const { incrementPage } = usePage();
  const { dataChunkSize: limit } = useDataChunkSize(PAGE_SIZE);
  const { similarParts } = useContext(SearchResultContext);

  const hasNextPage = similarParts && similarParts.length >= limit;

  if (isFetching) {
    return (
      <Flex h="50px" justify={"start"} align={"center"}>
        <InlineLoader size="sm" m="xs" />
        <Text>Chargement...</Text>
      </Flex>
    );
  }

  if (hasNextPage) {
    return (
      <Button variant={variant} style={style} onClick={incrementPage} {...props}>
        {t("search.seeMore")}...
      </Button>
    );
  }
};

const handleEstimate = ({
  quoteRequestLineId,
  updateCluster,
  cluster,
}: {
  quoteRequestLineId: string | undefined;
  updateCluster: ReturnType<typeof useUpdateCluster>["updateCluster"];
  cluster: TargetDto[];
}) => {
  if (!quoteRequestLineId) {
    showErrorToast({ title: "Aucune pièce n'a été chargée." });

    return;
  }

  updateCluster({
    sourceId: quoteRequestLineId,
    cluster: cluster.map((quoteRequestLine) => quoteRequestLine.id),
  });
};

SearchResult.CompareButton = function SearchResultCompareButton({
  style,
  ...props
}: {
  style?: CSSProperties;
}) {
  const { cluster, sourceQuoteRequestLine } = useContext(SearchResultContext);
  const navigate = useNavigate();

  const { updateCluster, isPending } = useUpdateCluster({
    onSuccess: () =>
      sourceQuoteRequestLine?.id &&
      navigate(ROUTES.partnerQuoteRequestLineCompareCluster(sourceQuoteRequestLine.id)),
  });

  return (
    <Button
      style={style}
      loading={isPending}
      onClick={() =>
        handleEstimate({
          quoteRequestLineId: sourceQuoteRequestLine?.id,
          updateCluster,
          cluster,
        })
      }
      rightSection={<Icon name="arrowRight" size={20} />}
      {...props}
    >
      {"Comparer les pièces sélectionnées"}
    </Button>
  );
};
